import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FaPlus, FaTrash, FaFileExcel, FaCheckCircle, FaEdit, FaSave, FaTimes, FaLock, FaPaperclip } from "react-icons/fa";
import api from "../../../libs/axios";
import { useAuth } from "../../../provider/auth.provider";
import { useConfirm } from "../../../provider/confirm.provider";
import { ExcelDownload } from "../../../utils/excel.utils";
import IncFilesPanel from "../components/IncFilesPanel";

const BILLING_DOCUMENT_TYPES = [
  { value: "invoice", label: "Invoice" },
  { value: "po", label: "PO" },
  { value: "wcc", label: "WCC" },
  { value: "client_signoff", label: "Client Sign-off" },
  { value: "other", label: "Other" },
];

// Invoicing and payment are tracked separately — an invoice can sit raised and
// unpaid for weeks, and cancelling one says nothing about money already received.
// A billing row is only added once an invoice has been raised, so a row IS an
// invoice: there is no "pending" any more, and a new record starts as Invoiced.
// Whether the money arrived is what the payment status says.
const INVOICE_STATUS = [
  { value: "invoiced", label: "Invoiced" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_STATUS = [
  { value: "unpaid", label: "Unpaid" },
  { value: "partially_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
];

const INVOICE_BADGE: Record<string, string> = {
  invoiced: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
};

const PAYMENT_BADGE: Record<string, string> = {
  unpaid: "bg-gray-200 text-gray-700",
  partially_paid: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-800",
};

const EMPTY_FORM = {
  po_number: "",
  invoice_number: "",
  invoice_date: "",
  invoice_amount: "",
  gst_amount: "",
  wcc_number: "",
  invoice_status: "invoiced",
  payment_status: "unpaid",
  remarks: "",
};

const num = (v: any) => Number(v || 0);
const money = (v: any) => num(v).toFixed(2);
const dateText = (v: any) => (v ? new Date(v).toLocaleDateString() : "");
const labelOf = (list: { value: string; label: string }[], v: string) =>
  list.find((s) => s.value === v)?.label || v || "";

interface BillingTabProps {
  siteId: string;
  // Current site so the tab knows whether billing is already closed out.
  site?: any;
  // Lets the parent refresh the header/stepper once billing completes.
  onWorkflowChange?: () => void;
}

const BillingTab: React.FC<BillingTabProps> = ({ siteId, site, onWorkflowChange }) => {
  const { hasPermission } = useAuth();
  const { confirm } = useConfirm();
  // Once billing is finalized the whole site is frozen: the API answers 423 on
  // every write, so every control below is folded behind this flag instead of
  // being left on screen to fail. Viewing and exporting still work.
  const isLocked = !!site?.is_locked;
  // Opening the tab, adding a record and removing one.
  const canManage = hasPermission("IncSite", "billing") && !isLocked;
  // Editing an existing record — invoice/GST figures and the two statuses.
  const canEdit = hasPermission("IncSite", "billing_edit") && !isLocked;
  const canExport = hasPermission("IncSite", "export_billing");
  const canComplete = hasPermission("IncSite", "billing_complete") && !isLocked;
  // Finalizing is the irreversible one — separate permission, separate button.
  const canFinalize = hasPermission("IncSite", "billing_finalize") && !isLocked;
  // Uploading / deleting billing documents; viewing only needs the tab.
  const canManageDocs = hasPermission("IncSite", "billing_files") && !isLocked;
  const [rows, setRows] = useState<any[]>([]);
  // One billing row's documents open at a time, shown as a row beneath it.
  const [openDocsFor, setOpenDocsFor] = useState<number | null>(null);
  const [docCounts, setDocCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [showExport, setShowExport] = useState(false);

  // Inline edit of one billing row. One at a time, so "save" is unambiguous.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inc/sites/${siteId}/billings`);
      const data = res.data?.data || [];
      setRows(data);
      setDocCounts(Object.fromEntries(data.map((b: any) => [b.id, Number(b.document_count || 0)])));
    } catch {
      toast.error("Failed to load billing records");
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    load();
  }, [load]);

  // Kept in sync with the server, which derives the same figure on write.
  const formTotal = num(form.invoice_amount) + num(form.gst_amount);

  const add = async () => {
    setSaving(true);
    try {
      await api.post(`/inc/sites/${siteId}/billings`, {
        ...form,
        invoice_amount: num(form.invoice_amount),
        gst_amount: num(form.gst_amount),
        total_amount: formTotal,
        invoice_date: form.invoice_date || undefined,
      });
      toast.success("Billing added");
      setForm({ ...EMPTY_FORM });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to add billing");
    } finally {
      setSaving(false);
    }
  };

  // <input type="date"> needs YYYY-MM-DD; the API returns full ISO timestamps.
  const dateInput = (v: any) => (v ? new Date(v).toISOString().slice(0, 10) : "");

  const startEdit = (b: any) => {
    setEditingId(b.id);
    setEditForm({
      po_number: b.po_number || "",
      invoice_number: b.invoice_number || "",
      invoice_date: dateInput(b.invoice_date),
      invoice_amount: String(b.invoice_amount ?? ""),
      gst_amount: String(b.gst_amount ?? ""),
      wcc_number: b.wcc_number || "",
      invoice_status: b.invoice_status || "invoiced",
      payment_status: b.payment_status || "unpaid",
      remarks: b.remarks || "",
    });
  };

  const saveEdit = async (id: number) => {
    setSavingEdit(true);
    try {
      await api.put(`/inc/billings/${id}`, {
        ...editForm,
        invoice_amount: num(editForm.invoice_amount),
        gst_amount: num(editForm.gst_amount),
        total_amount: num(editForm.invoice_amount) + num(editForm.gst_amount),
        invoice_date: editForm.invoice_date || undefined,
      });
      setEditingId(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update billing");
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (id: number) => {
    const docs = docCounts[id] || 0;
    const ok = await confirm({
      title: "Remove this billing record?",
      message: docs > 0
        ? `The record and its ${docs} attached document${docs === 1 ? "" : "s"} will be deleted.`
        : "The billing record will be deleted.",
      confirmText: "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/billings/${id}`);
      if (openDocsFor === id) setOpenDocsFor(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to remove billing");
    }
  };

  // Closing billing is the only way a site reaches the "Billing Done" stage.
  // The server requires at least one invoiced record; `force` covers money that
  // was settled outside the portal, and is confirmed explicitly.
  const completeBilling = async (force = false): Promise<void> => {
    setCompleting(true);
    try {
      const res = await api.post(`/inc/sites/${siteId}/billing/complete`, force ? { force: true } : {});
      const unpaid = res.data?.data?.unpaid_invoice_count || 0;
      toast.success(unpaid > 0 ? `Billing completed (${unpaid} invoice(s) still unpaid)` : "Billing completed");
      onWorkflowChange?.();
      load();
    } catch (e: any) {
      const message = e?.response?.data?.message || "Failed to complete billing";
      // The "nothing invoiced" refusal is the one case worth offering an override for.
      if (!force && message.includes("No invoiced billing record")) {
        setCompleting(false);
        const override = await confirm({
          title: "Nothing invoiced yet",
          message,
          warning: "Complete billing anyway? Use this only when the money was settled outside the portal.",
          confirmText: "Complete anyway",
          variant: "warning",
        });
        if (override) return completeBilling(true);
        return;
      }
      toast.error(message);
    } finally {
      setCompleting(false);
    }
  };

  // Finalize billing — the irreversible one.
  //
  // It closes the site out (like Complete Billing) AND locks it: from then on
  // nothing about the site may change again, in the measurement books, the BOQs,
  // the claims or the billing records. There is no unlock, so the confirmation
  // spells that out and the user has to click through it.
  const finalizeBilling = async (force = false): Promise<void> => {
    if (!force) {
      const ok = await confirm({
        title: "Finalize billing for this site?",
        message:
          "The site will be marked Completed and then locked. Its measurement books, BOQs, claims, visits, installed material and billing records all become read-only.",
        warning: "This cannot be undone — there is no unlock. Make sure everything is correct first.",
        confirmText: "Finalize & lock site",
        cancelText: "Not yet",
        variant: "lock",
      });
      if (!ok) return;
    }
    setFinalizing(true);
    try {
      const res = await api.post(`/inc/sites/${siteId}/billing/finalize`, force ? { force: true } : {});
      const unpaid = res.data?.data?.unpaid_invoice_count || 0;
      toast.success(
        unpaid > 0
          ? `Billing finalized — site locked (${unpaid} invoice(s) still unpaid)`
          : "Billing finalized — site locked",
      );
      onWorkflowChange?.();
      load();
    } catch (e: any) {
      const message = e?.response?.data?.message || "Failed to finalize billing";
      // Same single override as Complete Billing: money settled off-portal.
      if (!force && message.includes("No invoiced billing record")) {
        setFinalizing(false);
        const override = await confirm({
          title: "Nothing invoiced yet",
          message,
          warning:
            "Finalize and lock the site anyway? Use this only when the money was settled outside the portal. This cannot be undone.",
          confirmText: "Finalize anyway",
          variant: "lock",
        });
        if (override) return finalizeBilling(true);
        return;
      }
      toast.error(message);
    } finally {
      setFinalizing(false);
    }
  };

  const isClosed = site?.workflow_status === "closed";

  const exportRows = useMemo(
    () =>
      rows.map((b: any) => ({
        "PO Number": b.po_number || "",
        "Invoice Number": b.invoice_number || "",
        "Invoice Date": dateText(b.invoice_date),
        "Invoice Amount": num(b.invoice_amount),
        "GST Amount": num(b.gst_amount),
        "Total Amount": num(b.total_amount),
        "WCC Number": b.wcc_number || "",
        "Invoice Status": labelOf(INVOICE_STATUS, b.invoice_status),
        "Payment Status": labelOf(PAYMENT_STATUS, b.payment_status),
        Remarks: b.remarks || "",
      })),
    [rows],
  );

  const handleExport = () => {
    if (!exportRows.length) return toast.error("No billing records to export");
    setShowExport(true);
  };

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc: { invoice: number; gst: number; total: number }, b: any) => ({
          invoice: acc.invoice + num(b.invoice_amount),
          gst: acc.gst + num(b.gst_amount),
          total: acc.total + num(b.total_amount),
        }),
        { invoice: 0, gst: 0, total: 0 },
      ),
    [rows],
  );

  const inp = "px-2 py-2 text-sm border border-gray-300 rounded w-full";
  const lbl = "block text-xs text-gray-500 mb-1";

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;

  return (
    <div>
      {isLocked && (
        <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-3">
          Billing was finalized{site?.locked_at ? ` on ${new Date(site.locked_at).toLocaleDateString()}` : ""} — this
          site is locked. Billing records, measurement books, BOQs, claims and visits are all read-only from here on.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs text-gray-400">One PO can be linked with multiple INC sites.</p>
        <div className="flex flex-wrap items-center gap-2">
          {canExport && (
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
            >
              <FaFileExcel className="w-4 h-4" /> Export Excel
            </button>
          )}
          {canComplete && !isClosed && (
            <button
              onClick={() => completeBilling(false)}
              disabled={completing || rows.length === 0}
              title={rows.length === 0 ? "Add a billing record first" : "Close billing and complete this site"}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <FaCheckCircle className="w-4 h-4" /> {completing ? "Completing..." : "Complete Billing"}
            </button>
          )}
          {isClosed && !isLocked && (
            <span className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-800 bg-green-100 rounded">
              <FaCheckCircle className="w-4 h-4" /> Billing Completed
            </span>
          )}
          {/* Finalize is offered whether or not billing was "completed" first —
              it does the close-out itself, so this is a single terminal action. */}
          {canFinalize && (
            <button
              onClick={() => finalizeBilling(false)}
              disabled={finalizing || completing || rows.length === 0}
              title={
                rows.length === 0
                  ? "Add a billing record first"
                  : "Complete billing and lock this site permanently — no further changes anywhere"
              }
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-700 rounded hover:bg-purple-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              <FaLock className="w-4 h-4" /> {finalizing ? "Finalizing..." : "Finalize Billing"}
            </button>
          )}
          {isLocked && (
            <span className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-800 bg-purple-100 rounded">
              <FaLock className="w-4 h-4" /> Billing Finalized — Site Locked
            </span>
          )}
        </div>
      </div>

      {canManage && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className={lbl}>PO Number</label>
            <input placeholder="PO Number" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Invoice Number</label>
            <input placeholder="Invoice Number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Invoice Date</label>
            <input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Invoice Amount</label>
            <input placeholder="0.00" value={form.invoice_amount} onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })} className={inp} />
          </div>

          <div>
            <label className={lbl}>GST Amount</label>
            <input placeholder="0.00" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Total Amount</label>
            <input value={money(formTotal)} readOnly className={`${inp} bg-gray-100 text-gray-600 font-medium`} />
          </div>

          <div>
            <label className={lbl}>WCC Number</label>
            <input placeholder="WCC Number" value={form.wcc_number} onChange={(e) => setForm({ ...form, wcc_number: e.target.value })} className={inp} />
          </div>
          <div>
            <label className={lbl}>Invoice Status</label>
            <select value={form.invoice_status} onChange={(e) => setForm({ ...form, invoice_status: e.target.value })} className={inp}>
              {INVOICE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Payment Status</label>
            <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className={inp}>
              {PAYMENT_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Remarks</label>
            <input placeholder="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className={inp} />
          </div>

          <button
            onClick={add}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-800 rounded-md hover:bg-blue-900 disabled:bg-blue-400 justify-center"
          >
            <FaPlus className="w-3 h-3" /> Add Billing
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No billing records.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left whitespace-nowrap">PO Number</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Invoice Number</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Invoice Date</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Invoice Amount</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">GST Amount</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Total Amount</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">WCC Number</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Invoice Status</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Payment Status</th>
                <th className="px-3 py-2 text-left">Remarks</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Documents</th>
                {(canManage || canEdit) && <th className="px-3 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((b) =>
                editingId === b.id ? (
                  <tr key={b.id} className="bg-blue-50/40">
                    <td className="px-2 py-2"><input value={editForm.po_number} onChange={(e) => setEditForm({ ...editForm, po_number: e.target.value })} className={`${inp} min-w-[110px]`} /></td>
                    <td className="px-2 py-2"><input value={editForm.invoice_number} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} className={`${inp} min-w-[110px]`} /></td>
                    <td className="px-2 py-2"><input type="date" value={editForm.invoice_date} onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })} className={`${inp} min-w-[130px]`} /></td>
                    <td className="px-2 py-2"><input value={editForm.invoice_amount} onChange={(e) => setEditForm({ ...editForm, invoice_amount: e.target.value })} className={`${inp} min-w-[90px] text-right`} /></td>
                    <td className="px-2 py-2"><input value={editForm.gst_amount} onChange={(e) => setEditForm({ ...editForm, gst_amount: e.target.value })} className={`${inp} min-w-[90px] text-right`} /></td>
                    <td className="px-2 py-2 text-right font-medium text-gray-600 whitespace-nowrap">
                      {money(num(editForm.invoice_amount) + num(editForm.gst_amount))}
                    </td>
                    <td className="px-2 py-2"><input value={editForm.wcc_number} onChange={(e) => setEditForm({ ...editForm, wcc_number: e.target.value })} className={`${inp} min-w-[100px]`} /></td>
                    <td className="px-2 py-2">
                      <select value={editForm.invoice_status} onChange={(e) => setEditForm({ ...editForm, invoice_status: e.target.value })} className={`${inp} min-w-[110px]`}>
                        {INVOICE_STATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <select value={editForm.payment_status} onChange={(e) => setEditForm({ ...editForm, payment_status: e.target.value })} className={`${inp} min-w-[120px]`}>
                        {PAYMENT_STATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-2"><input value={editForm.remarks} onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })} className={`${inp} min-w-[120px]`} /></td>
                    <td className="px-2 py-2 text-gray-500 text-xs whitespace-nowrap">{docCounts[b.id] || 0} file(s)</td>
                    <td className="px-2 py-2">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => saveEdit(b.id)} disabled={savingEdit} title="Save" className="p-1.5 text-green-700 hover:bg-green-50 rounded disabled:opacity-60">
                          <FaSave className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditingId(null)} title="Cancel" className="p-1.5 text-gray-500 hover:bg-gray-100 rounded">
                          <FaTimes className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <React.Fragment key={b.id}>
                  <tr className={openDocsFor === b.id ? "bg-indigo-50/40" : undefined}>
                    <td className="px-3 py-2 whitespace-nowrap">{b.po_number || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{b.invoice_number || "-"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{dateText(b.invoice_date) || "-"}</td>
                    <td className="px-3 py-2 text-right">{money(b.invoice_amount)}</td>
                    <td className="px-3 py-2 text-right">{money(b.gst_amount)}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(b.total_amount)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{b.wcc_number || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${INVOICE_BADGE[b.invoice_status] || "bg-gray-100 text-gray-600"}`}>
                        {labelOf(INVOICE_STATUS, b.invoice_status)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${PAYMENT_BADGE[b.payment_status] || "bg-gray-100 text-gray-600"}`}>
                        {labelOf(PAYMENT_STATUS, b.payment_status)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 break-words max-w-xs">{b.remarks || "-"}</td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setOpenDocsFor((current) => (current === b.id ? null : b.id))}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded border transition-colors ${
                          openDocsFor === b.id
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                        }`}
                        title={openDocsFor === b.id ? "Hide documents" : "Show documents"}
                      >
                        <FaPaperclip className="w-3 h-3" /> {docCounts[b.id] || 0}
                      </button>
                    </td>
                    {(canManage || canEdit) && (
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          {canEdit && (
                            <button onClick={() => startEdit(b)} title="Edit" className="p-1.5 text-blue-700 hover:bg-blue-50 rounded">
                              <FaEdit className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canManage && (
                            <button onClick={() => remove(b.id)} title="Remove" className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                              <FaTrash className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {openDocsFor === b.id && (
                    <tr className="bg-indigo-50/40">
                      <td colSpan={canManage || canEdit ? 12 : 11} className="px-4 pb-4 pt-1">
                        <IncFilesPanel
                          filesUrl={`/inc/billings/${b.id}/files`}
                          deleteUrl={(fileId) => `/inc/billing-files/${fileId}`}
                          folderName="inc/billing-documents"
                          canManage={canManageDocs}
                          title={`Documents — ${b.invoice_number ? `Invoice ${b.invoice_number}` : `Billing #${b.id}`}`}
                          emptyLabel="No documents attached to this billing record yet."
                          documentTypes={BILLING_DOCUMENT_TYPES}
                          onCountChange={(count) => setDocCounts((p) => ({ ...p, [b.id]: count }))}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ),
              )}
            </tbody>
            <tfoot className="bg-gray-50 font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={3}>Total</td>
                <td className="px-3 py-2 text-right">{totals.invoice.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">{totals.gst.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">{totals.total.toFixed(2)}</td>
                <td colSpan={canManage || canEdit ? 6 : 5}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <ExcelDownload
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        data={exportRows}
        filename={`inc_billing_site_${siteId}`}
        title="Export Billing"
        description="Download INC billing records"
      />
    </div>
  );
};

export default BillingTab;
